import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, ArrowLeft, Check, Crown, Zap, BookOpen, FileText, HelpCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ProMembership({ user, onUpgrade }) {
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const navigate = useNavigate();

  const seoHelmet = (
    <Helmet>
      <title>Pro Membership - Unlimited EMRS KVS NVS Practice | Descriptive Test</title>
      <meta name="description" content="Upgrade to Pro membership for unlimited EMRS, KVS, NVS TGT PGT PRT exam preparation. Unlimited descriptive tests, mock tests, MCQ practice, AI evaluation, NCERT notes. Best value for teaching exam preparation 2026." />
      <meta name="keywords" content="Descriptive Test Pro, EMRS preparation premium, KVS exam premium plan, NVS exam subscription, TGT PGT premium practice, unlimited mock tests, unlimited descriptive test, teaching exam preparation plan" />
      <meta property="og:title" content="Pro Membership - Unlimited EMRS KVS NVS Practice" />
      <meta property="og:description" content="Unlimited descriptive tests, mock tests, MCQ practice for EMRS, KVS, NVS Tier-2 exams." />
      <link rel="canonical" href="https://descriptivetest.com/pro" />
    </Helmet>
  );

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleUpgrade = async (planId) => {
    setLoading(true);
    setSelectedPlan(planId);
    
    let order = null;
    
    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Failed to load payment gateway");
        setLoading(false);
        return;
      }

      // Create order
      const orderResponse = await axios.post(`${API}/payment/create-order`, {
        user_id: user.id,
        plan_id: planId
      });
      order = orderResponse.data;

      // Razorpay options
      const options = {
        key: order.key,
        amount: order.amount,
        currency: order.currency,
        name: "Descriptive Test",
        description: "Premium Plan - Full Access",
        order_id: order.orderId,
        handler: async function (response) {
          try {
            // Verify payment
            const verifyResponse = await axios.post(`${API}/payment/verify`, {
              user_id: user.id,
              plan_id: planId,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            if (verifyResponse.data.success) {
              toast.success("🎉 " + verifyResponse.data.message);
              onUpgrade(verifyResponse.data.user);
              navigate("/dashboard");
            }
          } catch (error) {
            toast.error(error.response?.data?.detail || "Payment verification failed");
          }
        },
        prefill: {
          name: user.name,
          email: user.email,
          contact: user.mobile || ""
        },
        theme: {
          color: "#f59e0b"
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
            setSelectedPlan(null);
          }
        }
      };

      // Open Razorpay checkout - NO DEMO MODE
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      setLoading(false);
      setSelectedPlan(null);
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.response?.data?.detail || error.message || "Payment initiation failed");
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  // Check if user already has premium
  const isPaidPlan = ["premium", "tier2_pro", "lokesh_notes", "unlimited", "basic", "standard", "paid"].includes(user.plan);
  
  // Handle unlimited plan (-1) properly
  const isUnlimited = user.questions_limit === -1 || user.plan === "unlimited";
  const questionsRemaining = isUnlimited ? "Unlimited" : Math.max(0, (user.questions_limit || 0) - (user.questions_used || 0));

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
      {seoHelmet}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_a586d417-0f0e-433d-918a-53924203f4fd/artifacts/y1n33gwl_Image%2016-12-25%20at%2010.38%E2%80%AFAM.jpeg" 
                alt="Logo" 
                className="w-10 h-10 rounded-lg"
              />
              <span className="text-xl font-bold bg-gradient-to-r from-amber-600 to-red-600 bg-clip-text text-transparent">
                Descriptive Test
              </span>
            </div>
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" data-testid="back-to-dashboard">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">
            Upgrade to Premium
          </h1>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Based on all <span className="font-semibold text-amber-600">CBSE Books</span>, <span className="font-semibold text-amber-600">KVS</span>, <span className="font-semibold text-amber-600">EMRS School Directory Questions</span> and <span className="font-semibold text-amber-600">Previous Year Papers</span>
          </p>
          
          {user.plan && user.plan !== 'free' && (
            <div className="mt-4">
              <Badge className="bg-green-100 text-green-800 px-4 py-2 text-sm">
                Current: PREMIUM | Questions: {questionsRemaining} remaining
              </Badge>
            </div>
          )}
        </div>

        {/* Free User Benefits */}
        <Card className="mb-6 border-gray-200 bg-gray-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-gray-500" />
              Free Users Get
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-white rounded-lg border">
                <div className="text-2xl font-bold text-gray-700">1</div>
                <div className="text-xs text-gray-500">Free Notes Topic</div>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <div className="text-2xl font-bold text-gray-700">1</div>
                <div className="text-xs text-gray-500">Free MCQ Mock</div>
              </div>
              <div className="p-3 bg-white rounded-lg border">
                <div className="text-2xl font-bold text-gray-700">1</div>
                <div className="text-xs text-gray-500">Free Descriptive</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Plan */}
        <Card className="mb-6 border-2 border-amber-400 shadow-xl bg-gradient-to-br from-white to-amber-50 overflow-hidden relative">
          <div className="absolute top-0 right-0 bg-gradient-to-l from-amber-500 to-orange-500 text-white px-6 py-1 text-sm font-bold rounded-bl-lg">
            RECOMMENDED
          </div>
          
          <CardHeader className="pb-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shadow-lg">
                <Crown className="w-8 h-8" />
              </div>
              <div>
                <CardTitle className="text-2xl text-gray-800">PREMIUM PLAN</CardTitle>
                <CardDescription className="text-base text-gray-600">
                  Complete exam preparation package
                </CardDescription>
              </div>
              <div className="ml-auto text-right">
                <div className="text-4xl font-bold text-amber-600">₹499</div>
                <div className="text-sm text-gray-500">one-time</div>
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              {/* Notes */}
              <div className="bg-white rounded-xl p-4 border border-amber-200">
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="w-5 h-5 text-amber-600" />
                  <span className="font-semibold text-gray-800">Notes Access</span>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>All Topics Unlocked</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>PDF Download</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="font-semibold text-amber-600">UNLIMITED Access</span>
                  </li>
                </ul>
              </div>
              
              {/* MCQ */}
              <div className="bg-white rounded-xl p-4 border border-amber-200">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-5 h-5 text-amber-600" />
                  <span className="font-semibold text-gray-800">MCQ Practice</span>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>All Subjects</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="font-semibold text-amber-600">UNLIMITED per Topic</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>1 Year Validity</span>
                  </li>
                </ul>
              </div>
              
              {/* OCR/Descriptive */}
              <div className="bg-white rounded-xl p-4 border border-amber-200">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-5 h-5 text-amber-600" />
                  <span className="font-semibold text-gray-800">Descriptive (OCR)</span>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="font-semibold text-amber-600">300 Questions</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>AI Evaluation</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>1 Year Validity</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <Button
              onClick={() => handleUpgrade("premium")}
              disabled={loading || isPaidPlan}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold py-4 text-lg shadow-lg"
              data-testid="upgrade-premium-button"
            >
              {loading && selectedPlan === "premium" ? (
                "Processing..."
              ) : isPaidPlan ? (
                "Already Subscribed"
              ) : (
                "Get PREMIUM PLAN - ₹499"
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Top-up Plan */}
        <Card className={`mb-6 border ${isPaidPlan ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-100 opacity-60'}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl ${isPaidPlan ? 'bg-gradient-to-br from-blue-400 to-indigo-500' : 'bg-gray-400'} flex items-center justify-center text-white`}>
                  <Zap className="w-6 h-6" />
                </div>
                <div>
                  <CardTitle className="text-xl text-gray-800">TOP-UP PACK</CardTitle>
                  <CardDescription>Extra descriptive questions</CardDescription>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-600">₹50</div>
                <div className="text-sm text-gray-500">per pack</div>
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            <div className="flex items-center justify-between mb-4 p-3 bg-white rounded-lg border">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                <span className="font-medium">+40 Descriptive Questions</span>
              </div>
              <Badge variant="outline" className="text-blue-600 border-blue-300">
                Added to your account
              </Badge>
            </div>
            
            <Button
              onClick={() => handleUpgrade("topup_40")}
              disabled={loading || !isPaidPlan}
              variant={isPaidPlan ? "default" : "secondary"}
              className={`w-full ${isPaidPlan ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-400'} text-white font-bold py-3`}
              data-testid="topup-button"
            >
              {loading && selectedPlan === "topup_40" ? (
                "Processing..."
              ) : !isPaidPlan ? (
                "Buy Premium Plan First"
              ) : (
                "Buy Top-up - ₹50"
              )}
            </Button>
            
            {!isPaidPlan && (
              <p className="text-xs text-gray-500 text-center mt-2">
                Top-up is available only for Premium subscribers
              </p>
            )}
          </CardContent>
        </Card>

        {/* Trust Badges */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-lg p-3 text-center border">
            <div className="text-xl mb-1">📚</div>
            <div className="text-xs text-gray-600">CBSE Based</div>
          </div>
          <div className="bg-white rounded-lg p-3 text-center border">
            <div className="text-xl mb-1">🏫</div>
            <div className="text-xs text-gray-600">KVS/EMRS</div>
          </div>
          <div className="bg-white rounded-lg p-3 text-center border">
            <div className="text-xl mb-1">📝</div>
            <div className="text-xs text-gray-600">PYQ Included</div>
          </div>
          <div className="bg-white rounded-lg p-3 text-center border">
            <div className="text-xl mb-1">🔒</div>
            <div className="text-xs text-gray-600">Secure Payment</div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-sm text-gray-500 mb-2">
            By purchasing, you agree to our refund policy
          </p>
          <Link to="/terms" className="text-amber-600 hover:underline text-sm font-medium">
            Read Terms of Service & Refund Policy →
          </Link>
        </div>
      </main>
    </div>
  );
}
